import { ref, watch } from 'vue'
import { defaultReportError } from '@/composables/useErrorLogger'
import {
  activateFirstAvailableCustomPage,
  DEFAULT_SCREEN_DELAY,
  toDevicePages,
  getBaseScreen,
  getScreenPictureUploadFile,
  isCustomSlot,
  isHomeSlot,
  normalizePageOrders,
} from '../utils/pages'
import { useBaseState } from './useBaseState'

import type { ScreenPictureItem } from '../utils/types'
import { toast } from '@rapoo/element'

export function usePages() {
  const {
    pages,
    pageConfig,
    homePresets,
    activeHomePresetStyle,
    pictures,
    activePage,
    loading,
    syncPages,
    refreshPages,
    loadPictures,
  } = useBaseState()

  void refreshPages()

  const pendingHomePresetStyle = ref<number | null>(null)
  const selectedPicture = ref<ScreenPictureItem | null>(null)
  const uploading = ref(false)
  const uploadProgress = ref(0)

  watch(pageConfig, () => {
    pendingHomePresetStyle.value = null
    selectedPicture.value = null
  })

  /** 按 manifest 分配物理槽位，并将待上传项追加到当前播放顺序。 */
  const setNewCarouselPage = () => {
    const result = activateFirstAvailableCustomPage(pages.value, pageConfig.value)
    if (!result) return

    pages.value = result.pages
    activePage.value = result.slot
  }

  /** 给首屏设置预设屏显 */
  const setActivatePreset = async () => {
    const baseScreen = getBaseScreen()
    if (!baseScreen) return
    if (!isHomeSlot(pageConfig.value, activePage.value)) return

    const style = pendingHomePresetStyle.value
    if (style === null || !homePresets.value.some((preset) => preset.style === style)) return

    try {
      await baseScreen.setIndexStyle(style)
      activeHomePresetStyle.value = style
      pendingHomePresetStyle.value = null
      await syncPages()
    } catch (err) {
      console.error('Failed to set homepage screen')
      defaultReportError(`Failed to set homepage screen: ${JSON.stringify(err)}`)
    }
  }

  /** 设置屏显（新增/覆盖） */
  const setPage = async () => {
    if (uploading.value) return

    const baseScreen = getBaseScreen()
    if (!baseScreen) return

    if (!isCustomSlot(pageConfig.value, activePage.value)) return

    const selected = selectedPicture.value
    if (!selected) return

    const pageIndex = pages.value.findIndex((item) => item.slot === activePage.value)
    if (pageIndex === -1) return

    const slot = activePage.value

    try {
      uploading.value = true
      uploadProgress.value = 0
      const uploadFile = await getScreenPictureUploadFile(selected)
      await baseScreen.uploadFile(slot, uploadFile, {
        onProgress: (progress) => {
          uploadProgress.value = Math.min(100, Math.max(0, Math.round(progress)))
        },
      })

      const devicePages = toDevicePages(
        normalizePageOrders(
          pages.value.map((page) =>
            page.slot === slot
              ? {
                  ...page,
                  id: selected.id,
                  delay: page.delay >= 3 ? page.delay : DEFAULT_SCREEN_DELAY,
                  show: true,
                }
              : page,
          ),
        ),
      )

      await baseScreen.pages.setPages(devicePages)

      toast.success('上传成功')
      selectedPicture.value = null
      await loadPictures()
      await syncPages()
    } catch (err) {
      console.error('Failed to set screen', err)
      defaultReportError(`Failed to set screen: ${JSON.stringify(err)}`)
    } finally {
      uploading.value = false
      uploadProgress.value = 0
    }
  }

  /** 删除当前激活的屏显 */
  const deletePage = async (slot: number) => {
    const pageIndex = pages.value.findIndex((page) => page.slot === slot)
    if (pageIndex === -1 || !isCustomSlot(pageConfig.value, slot) || !pages.value[pageIndex].show)
      return

    // 用来判断是否需要从设备中删除
    const isUploaded = !!pages.value[pageIndex].id
    const prevPage = pages.value[Math.max(pageIndex - 1, 0)]
    const nextActiveSlot =
      prevPage?.show && prevPage.slot !== slot
        ? prevPage.slot
        : (pages.value.find((page) => page.show && page.slot !== slot)?.slot ??
          pageConfig.value.homeSlot)

    if (!isUploaded) {
      const current = pages.value[pageIndex]

      if (current.thumbnail.startsWith('blob:')) {
        URL.revokeObjectURL(current.thumbnail)
      }

      pages.value[pageIndex] = {
        id: '',
        slot: current.slot,
        delay: DEFAULT_SCREEN_DELAY,
        order: current.order,
        show: false,
        kind: 'custom',
        thumbnail: '',
        thumbname: '',
        thumbext: '',
        error: false,
      }
      const [item] = pages.value.splice(pageIndex, 1)
      pages.value.push(item)
      pages.value = normalizePageOrders(pages.value)
      activePage.value = nextActiveSlot
      return
    }

    const baseScreen = getBaseScreen()
    if (!baseScreen) return

    try {
      loading.value = true
      await baseScreen.deleteImage(slot)
      toast.success('删除成功')
      await syncPages()
      activePage.value =
        pages.value.find((page) => page.slot === nextActiveSlot && page.show)?.slot ??
        pages.value.find((page) => page.show)?.slot ??
        pageConfig.value.homeSlot
    } catch (err) {
      console.error('Failed to delete screen')
      defaultReportError(`Failed to delete screen: ${JSON.stringify(err)}`)
    } finally {
      loading.value = false
    }
  }

  return {
    pages,
    pageConfig,
    homePresets,
    pictures,
    activePage,
    activeHomePresetStyle,
    pendingHomePresetStyle,
    selectedPicture,
    loading,
    uploading,
    uploadProgress,
    getPictureList: loadPictures,
    setActivatePreset,
    setNewCarouselPage,
    setPage,
    deletePage,
  }
}
